import React, { useMemo, useState } from 'react';
import { TOOL_LABELS } from './CommunityBetTracker';

// Per-tool accent colours + short chip labels
const TOOL_COLORS = {
    DISCORD_BOT: '#5865F2',
    SNIPE_FEED: '#10b981',
    ODDS_SCREEN: '#3b82f6',
    HOT_PROPS: '#f97316',
    MULTI_BUILDER: '#a855f7',
};

const TOOL_SHORT = {
    DISCORD_BOT: 'BOT',
    SNIPE_FEED: 'FEED',
    ODDS_SCREEN: 'ODDS',
    HOT_PROPS: 'HOT',
    MULTI_BUILDER: 'MULTI',
};

// Exact unit formatting (up to 2dp, trailing zeros stripped)
const cleanUnits = (n) => {
    const rounded = Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2).replace(/\.?0+$/, '');
};

function ToolBreakdown({ bets, unitSize = 1 }) {
    const [sortBy, setSortBy] = useState('pl');

    const toolData = useMemo(() => {
        const grouped = {};

        bets.forEach(bet => {
            const tool = bet.tipped_tool || 'UNKNOWN';
            const divisor = unitSize || 1;

            if (!grouped[tool]) {
                grouped[tool] = {
                    key: tool,
                    name: TOOL_LABELS[tool] || tool,
                    bets: 0,
                    wins: 0,
                    losses: 0,
                    totalStaked: 0,
                    totalReturned: 0,
                    pl: 0
                };
            }

            grouped[tool].bets++;
            grouped[tool].totalStaked += bet.stake / divisor;
            grouped[tool].pl += bet.pl / divisor;

            if (bet.result !== 'pending') {
                grouped[tool].totalReturned += (bet.stake + bet.pl) / divisor;
            }
            if (bet.result === 'win') grouped[tool].wins++;
            if (bet.result === 'loss') grouped[tool].losses++;
        });

        return Object.values(grouped)
            .map(t => ({
                ...t,
                winRate: (t.wins + t.losses) > 0 ? (t.wins / (t.wins + t.losses)) * 100 : 0,
                roi: t.totalStaked > 0 ? (t.pl / t.totalStaked) * 100 : 0
            }))
            .sort((a, b) => {
                if (sortBy === 'pl') return b.pl - a.pl;
                if (sortBy === 'roi') return b.roi - a.roi;
                return 0;
            });
    }, [bets, sortBy, unitSize]);

    const chartData = useMemo(() => [...toolData].sort((a, b) => b.pl - a.pl), [toolData]);

    const maxAbsPL = Math.max(...toolData.map(t => Math.abs(t.pl)), 1);
    const totalPL = toolData.reduce((sum, t) => sum + t.pl, 0);

    const formatCurrency = (value) => `${value >= 0 ? '+' : '-'}${cleanUnits(Math.abs(value))}U`;
    const formatStaked = (value) => `${cleanUnits(value)}U`;

    const getColor = (key) => TOOL_COLORS[key] || '#64748b';
    const getShort = (key, name) => TOOL_SHORT[key] || (name || key).substring(0, 4).toUpperCase();

    if (toolData.length === 0) return null;

    return (
        <div className="bg-[#0f172a] rounded border border-slate-700/50 overflow-hidden h-full">
            {/* Command Bar Header */}
            <div className="flex items-center justify-between px-3 md:px-4 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className={`w-1 h-4 ${totalPL >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <h3 className="text-xs md:text-sm font-semibold text-white tracking-tight whitespace-nowrap">Performance by Tool</h3>
                    <span className="hidden md:inline text-[10px] font-mono text-slate-600 tracking-widest uppercase">{toolData.length} tools</span>
                </div>
                <div className={`text-base md:text-lg font-bold font-mono tabular-nums ${totalPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(totalPL)}
                </div>
            </div>

            <div className="p-3 md:p-4">
                {/* P/L Distribution */}
                <div className="mb-4">
                    <div className="flex items-center gap-1.5 mb-3">
                        <div className="w-1 h-1 rounded-full bg-emerald-500" />
                        <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">P/L Distribution</div>
                    </div>
                    <div className="space-y-1.5">
                        {chartData.map((tool) => {
                            const barWidth = (Math.abs(tool.pl) / maxAbsPL) * 50;
                            const isPositive = tool.pl >= 0;
                            return (
                                <div key={tool.key} className="flex items-center gap-2 group" title={tool.name}>
                                    <div className="w-12 flex items-center justify-center">
                                        <div className="px-1 py-0.5 rounded text-[9px] font-mono font-bold text-white truncate max-w-full" style={{ backgroundColor: getColor(tool.key) }}>
                                            {getShort(tool.key, tool.name)}
                                        </div>
                                    </div>
                                    <div className="flex-1 flex items-center">
                                        <div className="w-1/2 flex justify-end">
                                            {!isPositive && <div className="h-4 bg-red-500/70 group-hover:bg-red-500 transition-all" style={{ width: `${barWidth}%` }} />}
                                        </div>
                                        <div className="w-px h-5 bg-slate-700" />
                                        <div className="w-1/2 flex justify-start">
                                            {isPositive && <div className="h-4 bg-emerald-500/70 group-hover:bg-emerald-500 transition-all" style={{ width: `${barWidth}%` }} />}
                                        </div>
                                    </div>
                                    <div className="w-16 text-right">
                                        <span className={`text-[11px] font-mono font-semibold tabular-nums ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatCurrency(tool.pl)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Performance Table */}
                <div className="border-t border-slate-700/50 pt-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-emerald-500" />
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">Performance</div>
                        </div>
                        <div className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">Click columns to sort</div>
                    </div>

                    <div className="-mx-3 md:-mx-4 overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-800/40 border-y border-slate-700/40">
                                <tr>
                                    {[
                                        { key: 'name', label: 'Tool', align: 'left', sortable: false, mobile: true },
                                        { key: 'bets', label: 'Tips', align: 'right', sortable: false, mobile: true },
                                        { key: 'record', label: 'W · L', align: 'center', sortable: false, mobile: false },
                                        { key: 'winRate', label: 'Win %', align: 'left', sortable: false, mobile: false },
                                        { key: 'staked', label: 'Staked', align: 'right', sortable: false, mobile: false },
                                        { key: 'roi', label: 'ROI', align: 'right', sortable: true, mobile: true },
                                        { key: 'pl', label: 'P/L', align: 'right', sortable: true, mobile: true },
                                    ].map(col => {
                                        const isActive = sortBy === col.key;
                                        const alignCls = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                                        const visibilityCls = col.mobile ? '' : 'hidden md:table-cell';
                                        const baseCls = `px-1.5 md:px-2 py-1.5 text-[9px] font-mono font-semibold tracking-widest uppercase ${alignCls} ${visibilityCls}`;
                                        return col.sortable ? (
                                            <th key={col.key} onClick={() => setSortBy(col.key)} className={`${baseCls} cursor-pointer transition-colors ${isActive ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>
                                                <span className="inline-flex items-center gap-1">
                                                    {col.label}
                                                    {isActive && <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 12 12"><path d="M6 8L2 4h8z" /></svg>}
                                                </span>
                                            </th>
                                        ) : (
                                            <th key={col.key} className={`${baseCls} text-slate-500`}>{col.label}</th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/30">
                                {toolData.map((tool) => {
                                    const winRateClamped = Math.min(Math.max(tool.winRate, 0), 100);
                                    return (
                                        <tr key={tool.key} className="hover:bg-slate-800/40 transition-colors">
                                            <td className="px-1.5 md:px-2 py-2">
                                                <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
                                                    <div className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold text-white flex-shrink-0" style={{ backgroundColor: getColor(tool.key) }}>
                                                        {getShort(tool.key, tool.name)}
                                                    </div>
                                                    <span className="text-[11px] text-white font-medium truncate">{tool.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-1.5 md:px-2 py-2 text-right text-[11px] font-mono text-slate-300 tabular-nums">{tool.bets}</td>
                                            <td className="hidden md:table-cell px-2 py-2 text-center text-[11px] font-mono tabular-nums">
                                                <span className="text-emerald-400">{tool.wins}</span>
                                                <span className="text-slate-600 mx-0.5">·</span>
                                                <span className="text-red-400">{tool.losses}</span>
                                            </td>
                                            <td className="hidden md:table-cell px-2 py-2">
                                                <div className="flex items-center gap-1.5 min-w-[90px]">
                                                    <div className="flex-1 h-1 bg-slate-700/50 rounded-sm overflow-hidden">
                                                        <div className={`h-full ${winRateClamped >= 50 ? 'bg-emerald-500/70' : 'bg-amber-500/70'}`} style={{ width: `${winRateClamped}%` }} />
                                                    </div>
                                                    <span className="text-[10px] font-mono text-slate-300 tabular-nums w-9 text-right">{tool.winRate.toFixed(1)}%</span>
                                                </div>
                                            </td>
                                            <td className="hidden md:table-cell px-2 py-2 text-right text-[10px] font-mono text-slate-400 tabular-nums">{formatStaked(tool.totalStaked)}</td>
                                            <td className="px-1.5 md:px-2 py-2 text-right">
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold tabular-nums ${tool.roi >= 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                                                    {tool.roi >= 0 ? '+' : ''}{tool.roi.toFixed(1)}%
                                                </span>
                                            </td>
                                            <td className={`px-1.5 md:px-2 py-2 text-right text-[11px] md:text-[12px] font-mono font-bold tabular-nums whitespace-nowrap ${tool.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {formatCurrency(tool.pl)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ToolBreakdown;
